import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchStoresThunk, activateStoreThunk, deactivateStoreThunk } from '../features/stores/storeThunks';
import Sidebar from '../Component/Sidebar';

const StoresPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { stores, loading, error } = useSelector(state => state.stores);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    dispatch(fetchStoresThunk());
  }, [dispatch]);

  // Filter stores based on search term and active status
  const filteredStores = stores?.filter(store => {
    const matchesSearch =
      store.store_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.store_city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.store_state?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesActive = showInactive ? true : store.is_active !== false;
    return matchesSearch && matchesActive;
  }) || [];

  // Handler for navigating to view store page
  const handleViewStore = (storeId) => {
    navigate(`/stores/${storeId}`);
  };

  // Handler for navigating to edit store page
  const handleEditStore = (storeId) => {
    navigate(`/stores/${storeId}/edit`);
  };

  // Handler for adding a new store
  const handleAddStore = () => {
    navigate('/storemanagementapp');
  };

  // Handler for toggling store active status
  const handleToggleActive = async (store) => {
    setTogglingId(store.id);
    try {
      if (store.is_active === false) {
        await dispatch(activateStoreThunk(store.id)).unwrap();
      } else {
        await dispatch(deactivateStoreThunk(store.id)).unwrap();
      }
    } catch (err) {
      // Error is handled in the slice
    } finally {
      setTogglingId(null);
    }
  };

  // Skeleton loader for store cards
  const StoreSkeletons = () => (
    <div className="store-team__stores-container">
      {[0, 1, 2].map((index) => (
        <div 
          className="store-team__store-card store-team__skeleton" 
          key={`skeleton-${index}`}
        >
          <div className="store-team__skeleton-image"></div>
          <div className="store-team__skeleton-content">
            <div className="store-team__skeleton-title"></div>
            <div className="store-team__skeleton-subtitle"></div>
            <div className="store-team__skeleton-buttons">
              <div className="store-team__skeleton-button"></div>
              <div className="store-team__skeleton-button"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="index-box">
      <Sidebar />
      <div className="store-team__page-container">
        <div className="store-team__content-section">
          {/* Updated Header with inline search */}
          <div className="store-team__header-controls">
            <div className="store-team__header-text">
              <h2 className="store-team__subtitle">Stores we provide safety too</h2>
              <h1 className="store-team__section-title">Always Here For You & Your Needs</h1>
            </div>

            <div className="store-team__header-actions">
              <div className="store-team__search-container">
                <div className="store-team__search-wrapper">
                  <svg 
                    className="store-team__search-icon" 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search stores by name or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="store-team__search-input"
                  />
                </div>
              </div>

              <button 
                className="store-team__add-store-btn" 
                onClick={handleAddStore}
              >
                + Add Store
              </button>

              <button
                className={`store-team__inactive-toggle-btn ${showInactive ? 'active' : ''}`}
                onClick={() => setShowInactive(prev => !prev)}
                title={showInactive ? 'Hide inactive stores' : 'Show inactive stores'}
              >
                {showInactive ? '👁 All Stores' : '👁 Active Only'}
              </button>
            </div>
          </div>

          {loading ? (
            <StoreSkeletons />
          ) : error ? (
            <div className="store-team__error">
              <p>Error: {error}</p>
            </div>
          ) : (
            <div className="store-team__stores-container">
              {filteredStores && filteredStores.length > 0 ? (
                filteredStores.map(store => (
                  <div 
                    className={`store-team__store-card ${store.is_active === false ? 'store-team__store-card--inactive' : ''}`}
                    key={store.id}
                  >
                    <div className="store-team__card-image">
                      <img 
                        src={store.image || "/api/placeholder/400/200"} 
                        alt={store.store_name} 
                        className="store-team__store-image"
                      />
                      <span className={`store-team__status-badge ${store.is_active === false ? 'store-team__status-badge--inactive' : 'store-team__status-badge--active'}`}>
                        {store.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                    
                    <div className="store-team__card-content">
                      <h3 className="store-team__store-title">{store.store_name}</h3>
                      
                      <p className="store-team__store-address">
                        {store.store_city || "City"}, {store.store_state || "State"}
                      </p>
                      
                      <div className="store-team__card-actions">
                        <button 
                          className="store-team__view-btn"
                          onClick={() => handleViewStore(store.id)}
                        >
                          View
                        </button>
                        <button 
                          className="store-team__edit-btn"
                          onClick={() => handleEditStore(store.id)}
                        >
                          Edit
                        </button>
                        <button
                          className={`store-team__toggle-btn ${store.is_active === false ? 'store-team__toggle-btn--enable' : 'store-team__toggle-btn--disable'}`}
                          onClick={() => handleToggleActive(store)}
                          disabled={togglingId === store.id}
                          title={store.is_active === false ? 'Enable this store' : 'Disable this store'}
                        >
                          {togglingId === store.id
                            ? '...'
                            : store.is_active === false
                            ? 'Enable'
                            : 'Disable'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : searchTerm ? (
                <div className="store-team__no-results">
                  <p>No stores found matching "{searchTerm}"</p>
                </div>
              ) : (
                <div className="store-team__no-stores">
                  <p>No stores available at the moment.</p>
                  <button 
                    className="store-team__add-first-store-btn" 
                    onClick={handleAddStore}
                  >
                    Add Your First Store
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <style jsx>{`
          .store-team__page-container {
            color: #333;
            padding: 0;
            background: #f8f9fa;
;
            width: 80%;
            border-radius: 25px;
          }

          .store-team__content-section {
            padding: 60px 20px;
            text-align: center;
          }

          /* Updated Header Controls - Now a flex row with three sections */
          .store-team__header-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 40px;
            gap: 20px;
            flex-wrap: wrap;
          }

          .store-team__header-text {
            flex: 0 0 auto;
            text-align: left;
            min-width: 280px;
          }

          /* Search + Add Store + Active Only move together as one group */
          .store-team__header-actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex-wrap: wrap;
            gap: 12px;
            flex: 1 1 520px;
            min-width: 0;
          }

          .store-team__header-actions .store-team__search-container {
            margin: 0;
          }

          .store-team__header-actions .store-team__add-store-btn {
            margin-left: 0;
          }

          .store-team__add-store-btn {
            background-color: #d72e59;
            color: white;
            border: none;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            flex-shrink: 0;
            margin-left: 15px;
          }

          .store-team__add-store-btn:hover {
            background-color: #b8244b;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }

          .store-team__subtitle {
            color: #d72e59;
            font-size: 18px;
            margin-bottom: 5px;
            font-weight: 500;
          }

          .store-team__section-title {
            font-size: 32px;
            margin-top: 0;
            margin-bottom: 10px;
            color: #333;
            font-weight: 600;
          }

          /* Updated Search Bar Styles - Now inline */
          .store-team__search-container {
            /* 240px basis: when there isn't room, the search + buttons wrap to
               their own row instead of the search squeezing to ~90px */
            flex: 1 1 240px;
            min-width: 240px;
            display: flex;
            justify-content: center;
            max-width: 400px;
            margin: 0 20px;
          }

          .store-team__search-wrapper {
            position: relative;
            width: 100%;
          }

          .store-team__search-icon {
            position: absolute;
            left: 16px;
            top: 50%;
            transform: translateY(-50%);
            color: #6B7280;
            z-index: 1;
          }

          .store-team__search-input {
            width: 100%;
            box-sizing: border-box; /* include padding + border in the 100%, or it spills under "Add Store" */
            padding: 12px 20px 12px 50px;
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            font-size: 15px;
            outline: none;
            transition: all 0.3s ease;
            background: #fff;
          }

          .store-team__search-input:focus {
            border-color: #d72e59;
            box-shadow: 0 0 0 3px rgba(215, 46, 89, 0.1);
          }

          .store-team__search-input::placeholder {
            color: #9CA3AF;
          }

          /* Updated Stores Container */
          .store-team__stores-container {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 24px;
            justify-items: center;
          }

          /* Updated Card Design - Left aligned content */
          .store-team__store-card {
            width: 100%;
            max-width: 350px;
            background: white;
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;
            border: 1px solid #f1f5f9;
          }

          .store-team__store-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
            border-color: #e2e8f0;
          }

          .store-team__card-image {
            width: 100%;
            height: 240px;
            overflow: hidden;
            background: #f8fafc;
          }

          .store-team__store-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.3s ease;
          }

          .store-team__store-card:hover .store-team__store-image {
            transform: scale(1.05);
          }

          .store-team__card-content {
            padding: 10px 24px 24px;
            text-align: left; /* Changed from center to left */
          }

          .store-team__store-title {
            font-size: 28px;
            font-weight: 600;
            color: #1f2937;
            margin: 0 0 2px 0;
            line-height: 1.3;
            text-align: left; /* Explicitly left aligned */
          }

          .store-team__store-address {
            color: #6b7280;
            font-size: 14px;
            margin: 0 0 24px 0;
            line-height: 1.4;
            font-weight: 400;
            text-align: left; /* Explicitly left aligned */
          }

          .store-team__card-actions {
            display: flex;
            gap: 12px;
          }

          .store-team__view-btn {
            flex: 1;
           background-color: #000000;
            color: white;
            border: none;
            padding: 12px 16px;
            border-radius: 12px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .store-team__view-btn:hover {
            background-color: #4b5563;
            transform: translateY(-1px);
          }

          .store-team__edit-btn {
            flex: 1;
           
              background-color: #D1D5DB;
            color: #6B7280;
            border: none;
            padding: 12px 16px;
            border-radius: 12px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .store-team__edit-btn:hover {
            background-color: #374151;
            transform: translateY(-1px);
          }

          /* Inactive card dimming */
          .store-team__store-card--inactive {
            opacity: 0.65;
            filter: grayscale(30%);
          }

          /* Status badge on card image */
          .store-team__status-badge {
            position: absolute;
            top: 12px;
            left: 12px;
            padding: 4px 10px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 0.3px;
            pointer-events: none;
          }

          .store-team__status-badge--active {
            background-color: rgba(16, 185, 129, 0.15);
            color: #065f46;
            border: 1px solid rgba(16, 185, 129, 0.4);
          }

          .store-team__status-badge--inactive {
            background-color: rgba(239, 68, 68, 0.15);
            color: #991b1b;
            border: 1px solid rgba(239, 68, 68, 0.4);
          }

          /* Make card-image position relative for badge */
          .store-team__card-image {
            position: relative;
          }

          /* Toggle enable/disable button */
          .store-team__toggle-btn {
            flex: 1;
            border: none;
            padding: 12px 16px;
            border-radius: 12px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .store-team__toggle-btn--disable {
            background-color: #fef2f2;
            color: #dc2626;
            border: 1px solid #fca5a5;
          }

          .store-team__toggle-btn--disable:hover:not(:disabled) {
            background-color: #fee2e2;
            transform: translateY(-1px);
          }

          .store-team__toggle-btn--enable {
            background-color: #f0fdf4;
            color: #16a34a;
            border: 1px solid #86efac;
          }

          .store-team__toggle-btn--enable:hover:not(:disabled) {
            background-color: #dcfce7;
            transform: translateY(-1px);
          }

          .store-team__toggle-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          /* Show/hide inactive toggle button */
          .store-team__inactive-toggle-btn {
            background-color: #f1f5f9;
            color: #475569;
            border: 1px solid #cbd5e1;
            padding: 10px 16px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            flex-shrink: 0;
          }

          .store-team__inactive-toggle-btn.active {
            background-color: #eff6ff;
            color: #1d4ed8;
            border-color: #93c5fd;
          }

          .store-team__inactive-toggle-btn:hover {
            background-color: #e2e8f0;
          }

          /* Skeleton Loading */
          .store-team__skeleton {
            animation: pulse 1.5s infinite ease-in-out;
            background: white;
          }

          .store-team__skeleton-image {
            height: 180px;
            background-color: #f1f5f9;
          }

          .store-team__skeleton-content {
            padding: 24px;
            text-align: left; /* Left aligned skeleton content */
          }

          .store-team__skeleton-title {
            height: 24px;
            width: 80%;
            background-color: #e2e8f0;
            border-radius: 6px;
            margin-bottom: 12px;
          }

          .store-team__skeleton-subtitle {
            height: 16px;
            width: 60%;
            background-color: #e2e8f0;
            border-radius: 4px;
            margin-bottom: 20px;
          }

          .store-team__skeleton-buttons {
            display: flex;
            gap: 12px;
          }

          .store-team__skeleton-button {
            flex: 1;
            height: 36px;
            background-color: #e2e8f0;
            border-radius: 8px;
          }

          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 0.8; }
            100% { opacity: 0.6; }
          }

          .store-team__error,
          .store-team__no-stores,
          .store-team__no-results {
            padding: 40px;
            background: #f8fafc;
            border-radius: 16px;
            margin: 20px auto;
            max-width: 600px;
            text-align: center;
          }

          .store-team__error {
            color: #dc2626;
            border: 1px solid #fecaca;
            background: #fef2f2;
          }

          .store-team__no-results {
            color: #6b7280;
          }

          .store-team__add-first-store-btn {
            background-color: #d72e59;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-top: 16px;
          }

          .store-team__add-first-store-btn:hover {
            background-color: #b8244b;
            transform: translateY(-1px);
          }

          /* Responsive Design */
          @media (max-width: 1024px) {
            .store-team__header-controls {
              flex-direction: column;
              align-items: stretch;
              gap: 20px;
            }

            .store-team__header-text {
              text-align: center;
              min-width: auto;
            }

            .store-team__search-container {
              margin: 0;
              max-width: none;
            }

            .store-team__add-store-btn {
              width: 100%;
            }

            .store-team__header-actions {
              flex-direction: column;
              align-items: stretch;
              flex: none;
            }

            .store-team__header-actions .store-team__search-container {
              flex: none;
              min-width: 0;
            }
          }

          @media (max-width: 768px) {
            .store-team__stores-container {
              grid-template-columns: 1fr;
              gap: 20px;
            }
            
            .store-team__store-card {
              width: 100%;
              max-width: none;
            }
            
            .store-team__section-title {
              font-size: 28px;
            }

            .store-team__card-actions {
              flex-direction: column;
            }

            .store-team__header-controls {
              gap: 15px;
            }
          }

          @media (max-width: 480px) {
            .store-team__content-section {
              padding: 40px 15px;
            }

            .store-team__card-content {
              padding: 20px;
            }

            .store-team__section-title {
              font-size: 24px;
            }

            .store-team__subtitle {
              font-size: 16px;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default StoresPage;